import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Settings } from '@/components/icons';

export interface ColumnOption {
  key: string;
  label: string;
  /** Locked columns are always shown (checkbox disabled). Typically the row
   *  identifier + any action-button column. */
  locked?: boolean;
  /** Can show "xxxxx" instead of its values (not action-button columns). */
  maskable?: boolean;
}

/** A table's sort: which column, which way. */
export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

interface Props {
  hidden: Set<string>;
  onChange: (next: Set<string>) => void;
  options: ColumnOption[];
  /** Columns whose values show as "xxxxx" (column stays). Omit both to hide the option. */
  masked?: Set<string>;
  onMaskChange?: (next: Set<string>) => void;
  /** Columns the table can be sorted by. Omit to hide the Sort section. */
  sortOptions?: { key: string; label: string }[];
  sort?: SortState | null;
  onSortChange?: (next: SortState | null) => void;
}

/**
 * Small chip button that opens a portal-rendered popover of checkboxes to
 * toggle table columns. Rendered to document.body so parent stacking
 * contexts (backdrop-blur cards, transformed containers, etc.) can't clip
 * or paint over it.
 */
export function ColumnPicker({ hidden, onChange, options, masked, onMaskChange, sortOptions, sort, onSortChange }: Props) {
  const canMask = !!masked && !!onMaskChange;
  const canSort = !!sortOptions?.length && !!onSortChange;
  const sortLabel = sort ? sortOptions?.find((o) => o.key === sort.key)?.label : undefined;
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (btnRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function place() {
      const b = btnRef.current?.getBoundingClientRect();
      if (b) setAnchor({ top: b.bottom + 4, right: window.innerWidth - b.right });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const visibleCount = options.filter((o) => !hidden.has(o.key)).length;

  function toggleMask(key: string) {
    if (!masked || !onMaskChange) return;
    const next = new Set(masked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onMaskChange(next);
  }

  function toggle(key: string) {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-color)] bg-white/[0.04] px-2.5 py-1 text-[11px] font-bold text-[var(--text-secondary)] hover:bg-accent/10 hover:text-accent"
        title={canSort ? 'Columns & sorting' : 'Show / hide columns'}
      >
        <Settings size={12} />
        {visibleCount}/{options.length} cols{canMask && masked!.size > 0 ? ` · ${masked!.size} hidden` : ''}{sortLabel ? ` · ${sortLabel} ${sort!.dir === 'asc' ? '↑' : '↓'}` : ''}
      </button>
      {open && anchor && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] w-72 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-2 shadow-2xl"
          style={{ top: anchor.top, right: anchor.right }}
        >
          {canSort && (
            <div className="mb-2 border-b border-[var(--border-color)] px-1 pb-2">
              <div className="mb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                <span>Sort by</span>
                {sort && (
                  <button type="button" className="text-[var(--text-secondary)] hover:text-accent" onClick={() => onSortChange!(null)}>
                    default
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <select
                  value={sort?.key ?? ''}
                  onChange={(e) => onSortChange!(e.target.value ? { key: e.target.value, dir: sort?.dir ?? 'asc' } : null)}
                  className="min-w-0 flex-1 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 py-1 text-xs font-semibold text-[var(--text-primary)] outline-none focus:border-accent"
                  aria-label="Sort by column"
                >
                  <option value="">Default order</option>
                  {sortOptions!.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <div className="flex rounded-md border border-[var(--border-color)] p-0.5" role="group" aria-label="Sort direction">
                  {(['asc', 'desc'] as const).map((d) => (
                    <button
                      key={d}
                      type="button"
                      disabled={!sort}
                      onClick={() => sort && onSortChange!({ key: sort.key, dir: d })}
                      className={`rounded px-2 py-0.5 text-[11px] font-bold transition disabled:opacity-40 ${sort?.dir === d ? 'bg-accent text-white' : 'text-[var(--text-secondary)] hover:text-accent'}`}
                      title={d === 'asc' ? 'A → Z, low → high, oldest first' : 'Z → A, high → low, newest first'}
                    >
                      {d === 'asc' ? '↑ Asc' : '↓ Desc'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="mb-1 flex items-center justify-between px-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
            <span>Toggle columns</span>
            <span className="flex items-center gap-3">
              {canMask && <span title="Show xxxxx instead of the column's values">Hide values</span>}
              <button
                type="button"
                className="text-[var(--text-secondary)] hover:text-accent"
                onClick={() => { onChange(new Set()); onMaskChange?.(new Set()); }}
              >
                reset
              </button>
            </span>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {options.map((o) => {
              const isVisible = !hidden.has(o.key);
              return (
                <label
                  key={o.key}
                  className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm ${o.locked ? 'opacity-60' : 'hover:bg-[var(--input-bg)]'}`}
                >
                  <input
                    type="checkbox"
                    checked={isVisible}
                    disabled={o.locked}
                    onChange={() => !o.locked && toggle(o.key)}
                  />
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.locked && <span className="text-[10px] text-[var(--text-muted)]">locked</span>}
                  {canMask && (
                    <input
                      type="checkbox"
                      className="ml-2"
                      checked={masked!.has(o.key)}
                      disabled={!o.maskable || !isVisible}
                      title={o.maskable ? `Show xxxxx instead of ${o.label} values` : "This column can't be masked"}
                      aria-label={`Hide ${o.label} values`}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleMask(o.key)}
                    />
                  )}
                </label>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
